import { useEffect, useRef, useState } from 'react'
import { Alert, Flex, Input, Modal, Skeleton, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { OperationResult } from '@/shared/api/operationResult'
import { formatDateTime } from '@/shared/lib/format'
import { formatMoneyValue, type Money } from '@/shared/lib/money'
import { LoadError } from '@/shared/ui/LoadError'
import { OperationResultAlert } from '@/shared/ui/OperationResultAlert'
import {
  SELFCARE_PASSWORD_MAX,
  SELFCARE_PASSWORD_MIN,
  fetchSelfcarePasswordStatus,
  setSelfcarePassword,
} from './selfcarePasswordApi'
import { subscriberKeys } from './subscriberKeys'
import {
  activateSubscriber,
  blockSubscriber,
  closeSubscriber,
  disconnectSessions,
  setPassword,
  setRateLimit,
  unblockSubscriber,
  type Subscriber,
} from './subscriberApi'
import { useSubscriberAction } from './useSubscriberAction'

export type ActionKind =
  | 'activate'
  | 'block'
  | 'unblock'
  | 'close'
  /** INTERNET (RADIUS/PPPoE) paroli — `PUT /api/subscribers/{id}/password` */
  | 'password'
  /**
   * MIJOZ KABINETI paroli — `POST /api/selfcare-admin/.../selfcare-password`.
   *
   * ⛔ `password` BILAN ARALASHTIRILMAYDI: ular ikki mustaqil maydon,
   * ikki mustaqil endpoint va ikki mustaqil oqibat. Sabab
   * `selfcarePasswordApi.ts` da yozilgan.
   */
  | 'selfcarePassword'
  | 'rateLimit'
  | 'disconnect'

interface SubscriberActionDialogProps {
  kind: ActionKind | null
  subscriber: Subscriber | null
  /**
   * Hozirgi QO'LDA qo'yilgan tezlik (`rateLimitOverride`), tarifniki
   * emas. ⚠️ Faqat kartochkadan ochilganda ma'lum — ro'yxat qatorida
   * bu ma'lumot umuman yuklanmagan. Noma'lum bo'lsa maydon bo'sh
   * boshlanadi va bu YOLG'ON emas: bo'sh qiymat «ustki qiymat yo'q»
   * degani va aynan shu yuboriladi.
   */
  rateLimitOverride?: string | null
  /**
   * Abonentning balansi — FAQAT `activate` ogohlantirishi uchun.
   *
   * ⚠️ `null` = NOMA'LUM (ledger javob bermagan), nol EMAS. Noma'lum
   * balansda ogohlantirish YOZILMAYDI: «balans bo'sh» degan jumla
   * o'shanda o'ylab topilgan bo'lardi.
   */
  balance?: Money | null
  onClose: () => void
}

/**
 * Abonent amallari — BITTA modal, tarkibi amalga qarab o'zgaradi.
 *
 * <h2>⚠️ Nega oltita alohida modal emas</h2>
 * Ular bir xil karkasni takrorlardi: sarlavha, natija banneri, ikki
 * tugma, «bajarilmoqda» holati va yopilganda holatni tozalash. Har
 * qatorda oltita `Modal` mount qilinishi ham shu bilan qo'shimcha
 * bo'lardi. Farq atigi ichkarida — bitta maydon yoki bitta jumla.
 *
 * <h2>⚠️ Natija oynada QOLADI (11-SHERIK-KABINETI-UI.md §4.4)</h2>
 * `message.success` ishlatilmaydi: u uch soniyada yo'qoladi, `PARTIAL`
 * esa aynan o'qilishi kerak bo'lgan holat — «baza yangilandi, lekin
 * faol sessiyaga qo'llanmadi». Uni ko'rmagan reseller amalni
 * bajarilmagan deb o'ylab qayta bosardi.
 */
export function SubscriberActionDialog({
  kind,
  subscriber,
  rateLimitOverride,
  balance = null,
  onClose,
}: SubscriberActionDialogProps) {
  const { t } = useTranslation()
  const action = useSubscriberAction()
  const [reason, setReason] = useState('')
  const [password, setPasswordValue] = useState('')
  const [rateLimit, setRateLimitValue] = useState('')

  // ⚠️ `useCallback` havolasi — `action` obyektining o'zi har chizishda
  // yangi bo'ladi va uni bog'liqlikka qo'yish effektni cheksiz
  // takrorlardi.
  const resetAction = action.reset

  /*
    ⚠️ Boshlang'ich tezlik REF orqali o'qiladi, bog'liqlik sifatida EMAS.

    Sabab o'lchanadigan: amal muvaffaqiyatli tugagach kesh bekor
    qilinadi, kartochka `rateLimitOverride` ni QAYTA yuklaydi va yangi
    qiymat bilan qaytadi. U bog'liqlikda tursa, effekt aynan shu payt
    qayta ishlab, natija bannerini o'chirib yuborardi — foydalanuvchi
    esa «bosdim, hech narsa bo'lmadi» deb ikkinchi marta bosardi.
  */
  const overrideRef = useRef(rateLimitOverride ?? '')
  overrideRef.current = rateLimitOverride ?? ''

  const subscriberId = subscriber?.id ?? null

  /*
    KABINET parolining holati — «bormi va qachon».

    ⚠️ FAQAT shu amal ochilganda so'raladi (`enabled`). Kartochkaga
    qo'yilsa u har ochilishda beshinchi so'rov bo'lardi va uni kassir
    telefonining sekin tarmog'i to'lardi; bu yerda esa u aynan qaror
    qabul qilinadigan payt keladi va HAR SAFAR yangi bo'ladi.

    ⚠️ Xato YUTILMAYDI. `set: false` («parol yo'q») va «so'rov
    yiqildi» — ikki boshqa narsa: birinchisida parol o'rnatish
    KERAK, ikkinchisida esa bazada nima turgani NOMA'LUM va uni
    almashtirish mavjud parolni bekorga bosib yozardi.
  */
  const selfcareStatus = useQuery({
    queryKey: subscriberKeys.selfcarePassword(subscriberId ?? ''),
    queryFn: ({ signal }) => fetchSelfcarePasswordStatus(subscriberId ?? '', signal),
    enabled: kind === 'selfcarePassword' && subscriberId !== null,
  })

  // Boshqa amal (yoki boshqa abonent) ochilganda hamma narsa boshiga
  // qaytadi — aks holda oldingi amalning natijasi yangisining ustida
  // turib, «bajarildi» degan yolg'on beradi.
  useEffect(() => {
    resetAction()
    setReason('')
    setPasswordValue('')
    setRateLimitValue(overrideRef.current)
  }, [kind, subscriberId, resetAction])

  if (kind === null || subscriber === null) return null

  /*
    ⚠️ IKKI PAROLNING ENG KAM UZUNLIGI BOSHQA-BOSHQA va bu taxmin
    emas, o'lchov: RADIUS paroli `SubscriberController.changePassword`
    da `min: 4`, kabinet paroli esa `CredentialServiceImpl` da 6
    (`MIN_PASSWORD_LENGTH`). Bitta songa keltirish qaysidir tomonda
    serverdan farq qilardi — qattiqroq bo'lsa ruxsat berilgan parolni
    to'sardi, yumshoqroq bo'lsa modal tepasida serverning xatosi
    chiqardi.
  */
  const isSelfcare = kind === 'selfcarePassword'
  const passwordMin = isSelfcare ? SELFCARE_PASSWORD_MIN : 4
  const passwordTooShort = password.trim().length < passwordMin
  const canSubmit = kind === 'password' || isSelfcare ? !passwordTooShort : true

  const run = () => {
    const call = (): Promise<OperationResult> => {
      switch (kind) {
        case 'activate':
          return activateSubscriber(subscriber.id)
        case 'block':
          return blockSubscriber(subscriber.id, reason.trim() || undefined)
        case 'unblock':
          return unblockSubscriber(subscriber.id)
        case 'close':
          return closeSubscriber(subscriber.id, reason.trim() || undefined)
        case 'password':
          return setPassword(subscriber.id, password)
        case 'selfcarePassword':
          return setSelfcarePassword(subscriber.id, password)
        case 'rateLimit':
          // ⚠️ Bo'sh maydon → `null`, `""` EMAS: `null` «ustki qiymatni
          // olib tashla, tarifniki ishlasin» degani, bo'sh satr esa
          // qiymat sifatida yozilib, tezlikni umuman yo'q qilishi mumkin.
          return setRateLimit(subscriber.id, rateLimit.trim() || null)
        case 'disconnect':
          return disconnectSessions(subscriber.login)
      }
    }
    void action.run(call).then((result) => {
      // Parol amalidan keyin maydon TOZALANADI: kiritilgan parol ochiq
      // oynada turib qolmasin (`CLAUDE.md` §7). ⚠️ IKKALA parol uchun
      // ham — kabinet paroli xuddi shunday sir.
      if (result !== null && (kind === 'password' || kind === 'selfcarePassword')) {
        setPasswordValue('')
      }
    })
  }

  const done = action.result !== null && action.result.status !== 'FAILED'

  /*
    ⚠️ OGOHLANTIRISH, TO'SIQ EMAS.

    Egasi «balansida pul bo'lsa faollashtirilsin» dedi, lekin SERVER
    bunday shart qo'ymaydi — u faqat `BLOCKED` ni tekshiradi
    (`SubscriberServiceImpl.activate`). Panelda o'zimizdan shart
    to'qish server RUXSAT BERGAN amalni jimgina to'sib qo'yardi va
    sababi hech qayerda yozilmagan bo'lardi. Shuning uchun tugma ochiq
    qoladi, matn esa oqibatni oldindan aytadi: bo'sh balansli abonentni
    enforcement qaytadan `SUSPENDED` ga tushirishi mumkin.

    ⚠️ `balance !== null` SHART: noma'lum balansda jim turiladi.
  */
  const emptyBalance =
    kind === 'activate' && balance !== null && balance.amount <= 0

  return (
    <Modal
      open
      title={t(`subscribers.action.${kind}.title`)}
      onCancel={onClose}
      okText={t(`subscribers.action.${kind}.confirm`)}
      cancelText={done ? t('common.close') : t('common.cancel')}
      confirmLoading={action.running}
      okButtonProps={{
        danger: kind === 'block' || kind === 'close',
        disabled: !canSubmit || done,
      }}
      onOk={run}
      destroyOnHidden
    >
      <Flex vertical gap={12}>
        <Typography.Text>
          {t(`subscribers.action.${kind}.text`, { login: subscriber.login })}
        </Typography.Text>

        {emptyBalance ? (
          <Alert
            type="warning"
            showIcon
            message={t('subscribers.action.activate.emptyBalance', {
              amount: formatMoneyValue(balance),
            })}
          />
        ) : null}

        {kind === 'block' || kind === 'close' ? (
          <Input.TextArea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder={t('subscribers.action.reasonPlaceholder')}
          />
        ) : null}

        {kind === 'password' ? (
          <>
            <Input.Password
              value={password}
              onChange={(e) => setPasswordValue(e.target.value)}
              autoComplete="new-password"
              maxLength={128}
              placeholder={t('subscribers.form.password')}
            />
            {/* ⚠️ Parol EKRANGA QAYTARILMAYDI — na natijada, na «nusxa
                olish» tugmasida. Backendda o'qish endpointi ham ataylab
                yo'q (`CLAUDE.md` §7). */}
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('subscribers.action.password.notShown')}
            </Typography.Text>
          </>
        ) : null}

        {isSelfcare ? (
          <>
            {/* ⚠️ HOLAT AVVAL, MAYDON KEYIN. Operator parolni
                almashtirishdan oldin uni umuman bor-yo'qligini
                bilishi kerak: «mijoz kabinetga kira olmayapti» ning
                eng ko'p uchraydigan sababi — parol HECH QACHON
                qo'yilmagani, va u holda almashtirish emas,
                O'RNATISH kerak. */}
            {selfcareStatus.isError ? (
              <LoadError
                titleKey="subscribers.action.selfcarePassword.statusFailed"
                error={selfcareStatus.error}
              />
            ) : null}
            {selfcareStatus.isPending ? <Skeleton active paragraph={{ rows: 1 }} /> : null}
            {selfcareStatus.isSuccess && !selfcareStatus.data.set ? (
              // ⚠️ Bu OGOHLANTIRISH, xato emas: parol yo'qligi normal
              // boshlang'ich holat, lekin oqibati aniq — mijoz kira
              // olmaydi.
              <Alert
                type="warning"
                showIcon
                message={t('subscribers.action.selfcarePassword.notSet')}
              />
            ) : null}
            {selfcareStatus.isSuccess && selfcareStatus.data.set ? (
              <Alert
                type="info"
                showIcon
                message={
                  selfcareStatus.data.updatedAt
                    ? t('subscribers.action.selfcarePassword.setAt', {
                        at: formatDateTime(selfcareStatus.data.updatedAt),
                      })
                    : // ⚠️ Sana NOMA'LUM bo'lsa u O'YLAB TOPILMAYDI:
                      // eski yozuvlarda `updatedAt` bo'sh bo'lishi
                      // mumkin va «bugun o'rnatilgan» degan taxmin
                      // operatorni chalg'itardi.
                      t('subscribers.action.selfcarePassword.setUnknown')
                }
              />
            ) : null}

            <Input.Password
              value={password}
              onChange={(e) => setPasswordValue(e.target.value)}
              autoComplete="new-password"
              maxLength={SELFCARE_PASSWORD_MAX}
              placeholder={t('subscribers.action.selfcarePassword.title')}
            />
            {/* ⚠️ PAROLNI PANEL YASAMAYDI — operator o'zi kiritadi.

                Tasodifiy yasash qulayroq ko'rinadi, lekin u parolni
                ekranga CHIQARISHNI talab qiladi (aks holda operator
                uni mijozga ayta olmaydi) — ya'ni ayni modalda bitta
                maydon parolni yashiradi, ikkinchisi ko'rsatadi.
                Egasining birinchi sharti aynan buni taqiqlaydi.
                Ustiga: yasalgan parol bir marta ko'rsatiladi va
                oyna yopilsa QAYTA O'QIB BO'LMAYDI (server xeshdan
                boshqa narsani saqlamaydi) — operator uni aytishga
                ulgurmasa, hammasini qaytadan qilardi. */}
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('subscribers.action.selfcarePassword.notShown')}
            </Typography.Text>
            {passwordTooShort && password !== '' ? (
              <Typography.Text type="danger" style={{ fontSize: 12 }}>
                {t('subscribers.action.selfcarePassword.tooShort', {
                  min: SELFCARE_PASSWORD_MIN,
                })}
              </Typography.Text>
            ) : null}
          </>
        ) : null}

        {kind === 'rateLimit' ? (
          <>
            <Input
              value={rateLimit}
              onChange={(e) => setRateLimitValue(e.target.value)}
              placeholder="10M/10M"
              allowClear
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('subscribers.action.rateLimit.emptyHint')}
            </Typography.Text>
          </>
        ) : null}

        {action.errorText ? (
          <Alert type="error" showIcon message={action.errorText} />
        ) : null}
        <OperationResultAlert result={action.result} />
      </Flex>
    </Modal>
  )
}
